package it.neckar.lizergy.model.financial.currency

import it.neckar.open.collections.fastSumByLong
import it.neckar.open.formatting.formatCents
import it.neckar.open.formatting.formatEuro
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.open.kotlin.lang.requireFinite
import it.neckar.open.unit.currency.EUR
import it.neckar.open.unit.currency.cct
import it.neckar.open.unit.currency.ct
import it.neckar.open.unit.number.IsFinite
import it.neckar.open.unit.other.pct
import kotlinx.serialization.Serializable
import kotlin.math.roundToLong

/**
 * Represents a money value
 */
@Serializable(with = MoneyFractionAsCentiCentsSerializer::class)
data class MoneyFraction
/**
 * Do not call this constructor directly.
 * Instead, use the factory methods.
 */
internal constructor(
  /**
   * The value in centi-cents (1/100 of a cent)
   */
  val centiCents: @cct Long,
) : Comparable<MoneyFraction> {

  /**
   * Returns a double value that represents the cents value.
   *
   * ATTENTION: Do *NOT* use this value for calculations to avoid rounding errors.
   */
  val cents: @ct Double
    get() = centiCents / CentFactor.toDouble()

  /**
   * Returns a double value that represents the euro value.
   *
   * ATTENTION: Do *NOT* use this value for calculations to avoid rounding errors.
   */
  val euros: @EUR Double
    get() = centiCents / EuroFactor.toDouble()

  /**
   * Formats this value as cents
   */
  @Deprecated("Formats as cents - use the method that makes it clear that it formats as cents")
  fun format(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return formatAsCents(i18nConfiguration, whitespaceConfig)
  }

  /**
   * Formats this value as cents
   */
  fun formatAsCents(i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String {
    return cents.formatCents(i18nConfiguration, whitespaceConfig)
  }

  fun formatAsEuros(i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    return euros.formatEuro(i18nConfiguration, whitespaceConfig)
  }

  /**
   * Returns the fallback value if the value is "0.0"
   */
  fun formatOrIfZero(fallbackIfZero: String, i18nConfiguration: I18nConfiguration = CurrentI18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
    if (euros == 0.0) return fallbackIfZero
    return format(i18nConfiguration, whitespaceConfig)
  }

  override operator fun compareTo(other: MoneyFraction): Int {
    return this.centiCents.compareTo(other.centiCents)
  }

  operator fun minus(other: MoneyFraction): MoneyFraction {
    return centiCents(this.centiCents - other.centiCents)
  }

  operator fun plus(other: MoneyFraction): MoneyFraction {
    return centiCents(this.centiCents + other.centiCents)
  }

  operator fun div(other: MoneyFraction): @pct Double {
    return this.centiCents.toDouble() / other.centiCents.toDouble()
  }

  fun divAndRound(other: Double): @pct MoneyFraction {
    other.requireFinite()
    return centiCents((this.centiCents / other).roundToLong())
  }

  fun timesAndRound(factor: Double): MoneyFraction {
    factor.requireFinite()
    return centiCents((this.centiCents * factor).roundToLong())
  }

  operator fun times(factor: Int): MoneyFraction {
    return centiCents((centiCents * factor))
  }

  operator fun unaryMinus(): MoneyFraction {
    return centiCents(-centiCents)
  }

  /**
   * Calculates the delta value - for debugging purposes
   */
  internal fun delta(other: MoneyFraction): @ct Long {
    val deltaInCentiCents = this.centiCents - other.centiCents
    return deltaInCentiCents
  }

  /**
   * Returns true if this value is zero
   */
  fun isZero(): Boolean {
    return centiCents == 0L
  }

  fun roundToCents(): Money {
    return Money.cents(cents.roundToLong())
  }

  override fun toString(): String {
    return formatAsCents(I18nConfiguration.US, WhitespaceConfig.Spaces)
  }

  companion object {
    val Zero: MoneyFraction = centiCents(0)

    /**
     * The factor used to convert cents to centi-cents
     */
    const val CentFactor: Int = 100

    /**
     * The factor used to convert euros to centi-cents
     */
    const val EuroFactor: Int = 10_000

    fun centiCents(centiCents: @cct Int): MoneyFraction {
      return MoneyFraction(centiCents = centiCents.toLong())
    }

    fun centiCents(centiCents: @cct Long): MoneyFraction {
      return MoneyFraction(centiCents = centiCents)
    }

    fun cents(cents: @IsFinite @ct Double): MoneyFraction {
      cents.requireFinite { "Finite value required but was <$cents>" }

      val centiCents = (cents * CentFactor).roundToLong()
      return MoneyFraction(centiCents = centiCents)
    }

    fun cents(cents: @ct Int): MoneyFraction {
      val centiCents = (cents * CentFactor).toLong()
      return MoneyFraction(centiCents = centiCents)
    }

    fun cents(cents: @ct Long): MoneyFraction {
      val centiCents = (cents * CentFactor).toLong()
      return MoneyFraction(centiCents = centiCents)
    }

    /**
     * Creates a new instance - rounds the value to two digits
     */
    fun euros(euros: @IsFinite @EUR Double): MoneyFraction {
      euros.requireFinite { "Finite value required but was <$euros>" }

      @cct val centiCents = (euros * EuroFactor).roundToLong()
      return MoneyFraction(centiCents)
    }

    /**
     * Calculates the sum for the given elements
     */
    inline fun <T> fastSumBy(elements: List<T>, callback: (value: T) -> MoneyFraction): MoneyFraction {
      val centiCents = elements.fastSumByLong { callback(it).centiCents }
      return centiCents(centiCents)
    }
  }
}


/**
 * Converts a euro value (as double) into a MoneyFraction
 */
val @EUR Double.euro100: MoneyFraction
  get() = MoneyFraction.euros(this)

/**
 * Converts a euro value (as int) into a MoneyFraction
 */
val @EUR Int.euro100: MoneyFraction
  get() = MoneyFraction.euros(this.toDouble())

/**
 * Converts a cent value (as double) into a MoneyFraction
 */
val @ct Double.cents100: MoneyFraction
  get() = MoneyFraction.cents(this)

val @ct Int.cents100: MoneyFraction
  get() = MoneyFraction.cents(this)

/**
 * Calculates the sum
 */
fun Iterable<MoneyFraction>.sum(): MoneyFraction {
  val totalCentiCents = sumOf { it.centiCents }
  return MoneyFraction.centiCents(totalCentiCents)
}
